/*
 * Netzwerk-Scan-Ansicht der Multi-Phase Discovery (concept C.10.a Slot 3).
 * Plant-scoped: der Route-Parameter :id liefert die Anlage — kein
 * Dropdown, kein globales Cross-Plant-Listing.
 *
 * Fluss: "Scan starten" schickt Trigger an alle Field-Clients der Anlage,
 * pollt danach `/scan/candidates` in kurzen Abständen. Master-Detail:
 * links Kandidaten-Tabelle, rechts Detail + Aktionen.
 */

import { useEffect, useMemo, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import type { ColumnDef } from '@tanstack/react-table';
import { AppShell } from '../components/AppShell';
import { DeviceClientInfo } from '../components/DeviceClientInfo/DeviceClientInfo';
import { PlantSubNav } from '../components/PlantSubNav';
import { showToast } from '../components/Toaster';
import { showConfirm } from '../components/Confirmer';
import { ClassifyCandidateModal } from '../components/ClassifyCandidateModal';
import { CredentialsModal } from '../components/CredentialsModal';
import { ApplySchemaTemplateModal } from '../components/ApplySchemaTemplateModal';
import { discoveryUnavailableReason, isModbusFamily } from '../api/protocols';
import { TriggerScanModal } from '../components/TriggerScanModal';
import { DataTable } from '../components/ui/DataTable';
import { useAsync } from '../hooks/useAsync';
import { api, ApiError } from '../api/client';
import { onDiscoveryProgress, subscribePlant } from '../realtime/hubClient';
import { sessionSignal } from '../auth/session';
import { directBadge, remoteWebAction, remoteWebLabel, type WebTarget } from '../components/remoteWebModel';
import { openRemoteWeb } from '../components/remoteWebApi';
import {
  DiscoveryRadar,
  NetworkScan,
  IconDiscovery,
  IconTemplateApply,
  IconSchemaRemove,
  IconDeleteSoft,
  IconCancel,
  IconSave,
} from '../icons/HvacIcons';
import './ScanCandidatesPage.css';

interface Props { id?: string }

interface ScanCandidateSummary {
  id: string;
  ip: string;
  mac: string | null;
  hostname: string | null;
  vendorHint: string | null;
  protocolHints: string[];
  interfaceName: string | null;
  bacnetDeviceInstance: number | null;
  bacnetDeviceName: string | null;
  deviceId: string | null;
  /** Actual protocol on the classified device (null until classified).
   *  Prefer this over protocolHints[0] when opening the credentials modal. */
  deviceProtocol: string | null;
  /** Operator-set device name once classified. Surfaced as the row label in
   *  "Verbundene Geräte" so the section shows the human name (e.g.
   *  "Bösch WP-Manager (DEOS)") instead of the scan-vendor guess. */
  deviceName: string | null;
  isSelf: boolean;
  firstSeenAt: string;
  lastSeenAt: string;
  /** Remote-Web (docs/remote-web.md): Linux-Brücke und Brückenport, an dem
   *  der Client das Gerät gelernt hat, und ob es direkt am Client hängt. */
  bruecke?: string | null;
  anschluss?: string | null;
  direkt?: boolean | null;
  /** Field-Client, über den das Gerät erreichbar ist (vom Server aus dem
   *  Netz abgeleitet), sonst null. */
  clientId?: string | null;
}

/** Auf `classify` bekommen wir Protokoll + Symbol + Name zurück, aber
 *  der ScanCandidateSummary hat nur `deviceId`. Für den CredentialsModal
 *  brauchen wir aber das Protokoll — wir halten es lokal fest, sobald
 *  entweder Klassifizierung passiert ist ODER wir per protocolHints eine
 *  eindeutige Auswahl ableiten können. */
interface CredentialsContext {
  deviceId: string;
  protocol: string;
  displayName: string;
  /** Scan-detected IP — the modal uses it to prefill host/URL fields so
   *  the technician doesn't have to type an address they already scanned. */
  ip: string;
}

interface DeviceCredentialsPresenceItem {
  deviceId: string;
  protocol: string;
  updatedAt: string;
}
interface ListDeviceCredentialsPresenceResponse {
  devices: DeviceCredentialsPresenceItem[];
}

/** Server: /api/plants/{id}/device-schema-presence — per device the node
 *  and leaf count. Used to gate Discovery/Template buttons: a device with
 *  materialized nodes/leaves already has a schema, no point running
 *  discovery on it again. */
interface DeviceSchemaPresenceItem {
  deviceId: string;
  nodeCount: number;
  leafCount: number;
  /** Name of the applied schema template. Null when the schema comes from
   *  Discovery or was hand-built; UI renders "Discovered Scheme" in that
   *  case. */
  appliedTemplateName: string | null;
  appliedTemplateVersion: number | null;
}
interface ListDeviceSchemaPresenceResponse {
  devices: DeviceSchemaPresenceItem[];
}

/** Scan-Laufzeit-Timeout: nach so vielen ms geben wir dem Field-Client
 *  auf, ohne dass wir wenigstens eine neu-geseh-ene Kandidatur registriert
 *  haben. Der Server-side TCP-Scanner läuft mit ConnectTimeout 400ms *
 *  254 Hosts * (Ports/Concurrency) ≈ 10-20s pro Interface plus BACnet-
 *  Peer-Probe-Fenster 3s. 30s ist eine großzügige Obergrenze. */
const SCAN_TIMEOUT_MS = 30_000;
/** Polling-Interval während eines aktiven Scans. Der Server aggregiert
 *  Reports in Bulks, kurze Intervalle sind ok. */
const SCAN_POLL_MS = 2_500;
/** Idle-Polling (kein Scan läuft): weniger häufig, um Server-Last klein
 *  zu halten aber gross genug damit UI ohne F5 aktuell bleibt. */
const IDLE_POLL_MS = 15_000;

interface ScanProgress {
  correlationId: string;
  triggeredAt: number;
}

/** Discovery-Progress: nach Klick auf "Discovery starten" gehalten bis der
 *  Field-Client mindestens eine Session commited hat, oder die Deadband
 *  läuft (kein Tick vom Client innerhalb DISCOVERY_DEADBAND_MS). Es gibt
 *  ABSICHTLICH keinen absoluten Timeout — reale Discoveries von großen
 *  Controllern (Climatix 300+ JsonIDs, BACnet 500+ Objekte) können mehrere
 *  Minuten dauern. Solange der Client Progress meldet, warten wir weiter. */
interface DiscoveryProgress {
  correlationId: string;
  triggeredAt: number;
  deviceId: string;
  deviceName: string;
  /** Letzter empfangener Tick — treibt die Deadband-Uhr. */
  lastTickAt: number;
  /** Phase-String vom letzten Tick (connecting/enumerating/reading/…). */
  phase: string;
  /** Menschenlesbarer Schritt vom letzten Tick. */
  step: string;
  completed: number | null;
  total: number | null;
}

/** Wenn N Millisekunden KEIN Progress-Tick reingekommen ist, betrachten
 *  wir die Discovery als steckengeblieben. Grosszügig gewählt: 30 s
 *  reicht auch für seltene Netzwerkhänger auf dem Field-Client. */
const DISCOVERY_DEADBAND_MS = 30_000;
/** Polling-Interval während wir auf eine neue Session warten. */
const DISCOVERY_POLL_MS = 1_500;

interface DiscoverySessionSummary {
  id: string;
  plantId: string;
  deviceId: string | null;
  deviceIdentifier: string;
  status: string;
  createdAt: string;
  committedAt: string | null;
}

export function ScanCandidatesPage({ id: plantId }: Props) {
  const [tick, setTick] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [classifying, setClassifying] = useState<ScanCandidateSummary | null>(null);
  const [triggerModalOpen, setTriggerModalOpen] = useState(false);
  const [credentialsFor, setCredentialsFor] = useState<CredentialsContext | null>(null);
  /** Anzahl registrierte Field-Clients auf dieser Anlage, gemeldet aus der
   *  DeviceClientInfo-Karte. Wenn >=1 verstecken wir die "Field-Client (dieser
   *  Anlage)" Zeilen unter "Verfügbare Geräte" weil die Karte oben genau
   *  dieselbe Box mit Interfaces + MAC schon reicher darstellt. */
  const [fieldClientCount, setFieldClientCount] = useState(0);
  /** Wenn nicht null, läuft gerade ein Scan. Actions gesperrt bis a) ein
   *  Kandidat mit lastSeenAt >= triggeredAt reinkommt, oder b) SCAN_TIMEOUT_MS
   *  seit dem Trigger vergangen sind. */
  const [scanProgress, setScanProgress] = useState<ScanProgress | null>(null);
  const [discoveryProgress, setDiscoveryProgress] = useState<DiscoveryProgress | null>(null);
  const location = useLocation();

  // Plant-Name für die SubNav lädt PlantSubNav jetzt selbst — kein
  // separater Fetch mehr in der Page nötig.

  // Wir laden IMMER alle (auch klassifizierte) und filtern client-side.
  // Das macht die Anzeige der zugewiesenen Kandidaten sofort umschaltbar
  // ohne Roundtrip und zeigt dem User dass da noch was ist statt "leer".
  const listState = useAsync(
    (signal) => plantId
      ? api.get<ScanCandidateSummary[]>(
          `/api/plants/${plantId}/scan/candidates?unassignedOnly=false`,
          signal)
      : Promise.resolve<ScanCandidateSummary[]>([]),
    [plantId, tick],
  );
  const allCandidates = listState.status === 'ok' ? listState.data : [];
  // Always show every candidate — assigned + open + self — the operator sees
  // the full room at a glance and the first-column icon tells them what's
  // what. The old "bereits zugewiesene mitzeigen" toggle was noise: no reason
  // to ever HIDE assigned rows when the icon marks them clearly.
  const candidates = allCandidates;
  const assignedCount = allCandidates.filter((c) => c.deviceId).length;
  const openCount = allCandidates.length - assignedCount;

  // Auto-Refresh Polling. Während eines Scans schneller (SCAN_POLL_MS),
  // idle langsamer (IDLE_POLL_MS). Der User braucht kein F5 mehr um die
  // Liste zu aktualisieren.
  useEffect(() => {
    if (!plantId) return;
    const interval = scanProgress ? SCAN_POLL_MS : IDLE_POLL_MS;
    const id = window.setInterval(() => setTick((n) => n + 1), interval);
    return () => window.clearInterval(id);
  }, [plantId, scanProgress]);

  // Scan-Erkennung: sobald mindestens ein Kandidat mit lastSeenAt >= dem
  // Trigger-Timestamp reinkommt, ist der Scan „done". Sonst nach
  // SCAN_TIMEOUT_MS Timeout mit Warn-Toast.
  useEffect(() => {
    if (!scanProgress) return;
    const seenAfterTrigger = allCandidates.some(
      (c) => new Date(c.lastSeenAt).getTime() >= scanProgress.triggeredAt,
    );
    if (seenAfterTrigger) {
      setScanProgress(null);
      return;
    }
    const remaining = scanProgress.triggeredAt + SCAN_TIMEOUT_MS - Date.now();
    if (remaining <= 0) {
      setScanProgress(null);
      showToast({
        tone: 'warn',
        title: 'Scan-Timeout',
        message: 'Der Client hat innerhalb von 30 s keine neuen Kandidaten gemeldet. Ist der Field-Client online?',
        correlationId: scanProgress.correlationId,
        autoHideAfterMs: 8000,
      });
      return;
    }
    const t = window.setTimeout(
      () => setTick((n) => n + 1),
      Math.min(remaining, 1_000),
    );
    return () => window.clearTimeout(t);
  }, [scanProgress, allCandidates]);

  // Presence-Map welche Devices Credentials auf File haben — steuert die
  // Enabled-Logik des "Discovery starten"-Buttons. Aktualisiert sich bei
  // jedem tick (nach Klassifizierung/Credentials-Save).
  const credentialsPresenceState = useAsync(
    (signal) => plantId
      ? api.get<ListDeviceCredentialsPresenceResponse>(
          `/api/plants/${plantId}/device-credentials`, signal)
      : Promise.resolve<ListDeviceCredentialsPresenceResponse>({ devices: [] }),
    [plantId, tick],
  );
  const devicesWithCreds = new Set<string>(
    credentialsPresenceState.status === 'ok'
      ? credentialsPresenceState.data.devices.map((d) => d.deviceId)
      : [],
  );

  // Schema-Presence: welche Devices haben Nodes/Leaves. Steuert die
  // Sichtbarkeit von Discovery/Template-Aktionen — ein Device mit Schema
  // braucht keinen Discovery-Trigger, dafür eher "Anderes Template" oder
  // "Schema entfernen".
  const schemaPresenceState = useAsync(
    (signal) => plantId
      ? api.get<ListDeviceSchemaPresenceResponse>(
          `/api/plants/${plantId}/device-schema-presence`, signal)
      : Promise.resolve<ListDeviceSchemaPresenceResponse>({ devices: [] }),
    [plantId, tick],
  );
  const schemaByDevice = new Map<string, {
    nodeCount: number;
    leafCount: number;
    appliedTemplateName: string | null;
    appliedTemplateVersion: number | null;
  }>();
  if (schemaPresenceState.status === 'ok') {
    for (const d of schemaPresenceState.data.devices) {
      schemaByDevice.set(d.deviceId, {
        nodeCount: d.nodeCount,
        leafCount: d.leafCount,
        appliedTemplateName: d.appliedTemplateName,
        appliedTemplateVersion: d.appliedTemplateVersion,
      });
    }
  }

  // "Anderes Template anwenden…" öffnet den ApplySchemaTemplateModal.
  const [applyTemplateFor, setApplyTemplateFor] = useState<{
    deviceId: string; protocol: string; hasSchema: boolean;
  } | null>(null);

  // "Schema entfernen" — hart, mit Bestätigung. Nach Erfolg refresh der
  // Presence-Maps damit die Buttons zurückspringen.
  const deleteDeviceSchema = async (deviceId: string, deviceName: string) => {
    if (!plantId) return;
    const ok = await showConfirm({
      title: 'Schema entfernen?',
      message: `Alle Nodes und Leaves unter „${deviceName}" werden hart gelöscht. Device, Credentials und Scan-Zuweisung bleiben. Kein Undo.`,
      confirmLabel: 'Schema löschen',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete<{ nodesDeleted: number; leavesDeleted: number }>(
        `/api/plants/${plantId}/devices/${deviceId}/schema`,
      );
      showToast({
        tone: 'success',
        title: 'Schema entfernt',
        message: `„${deviceName}": Baum ist wieder leer.`,
        autoHideAfterMs: 4000,
      });
      setTick((t) => t + 1);
    } catch (err) {
      const msg = err instanceof ApiError
        ? `HTTP ${err.status}` : (err as Error)?.message ?? 'Unbekannter Fehler';
      showToast({
        tone: 'error',
        title: 'Löschen fehlgeschlagen',
        message: msg,
        autoHideAfterMs: 6000,
      });
    }
  };

  // Sessions pro Device — für den Discovery-Icon-Link in der Aktionen-Spalte.
  // Wir bauen zwei Sets: existieren Sessions überhaupt (Link sichtbar), und
  // Preview-Sessions (Link mit Warn-Farbe damit der Operator merkt "da wartet
  // ein Review").
  const sessionsPresenceState = useAsync(
    (signal) => plantId
      ? api.get<DiscoverySessionSummary[]>(
          `/api/discovery/sessions?plantId=${plantId}`, signal)
      : Promise.resolve<DiscoverySessionSummary[]>([]),
    [plantId, tick],
  );
  const devicesWithSessions = new Set<string>();
  const devicesWithPreviewSession = new Set<string>();
  if (sessionsPresenceState.status === 'ok') {
    for (const s of sessionsPresenceState.data) {
      if (!s.deviceId) continue;
      devicesWithSessions.add(s.deviceId);
      const isPreview = typeof s.status === 'number' ? s.status === 0 : s.status === 'Preview';
      if (isPreview) devicesWithPreviewSession.add(s.deviceId);
    }
  }

  // Namens-Lookup für Umbenennen — der Rename-Modal braucht den aktuellen
  // Namen als Startwert. ScanCandidateSummary.deviceName kommt vom Server,
  // das reicht als Quelle.
  const [renameFor, setRenameFor] = useState<{ deviceId: string; currentName: string } | null>(null);

  const selected = candidates.find((c) => c.id === selectedId) ?? null;

  useEffect(() => {
    // Ausgewählter Kandidat existiert nicht mehr (z.B. gelöscht) → Auswahl fallen lassen.
    if (selectedId && !candidates.some((c) => c.id === selectedId)) setSelectedId(null);
  }, [candidates, selectedId]);

  // Trigger sitzt im TriggerScanModal (Interface-Auswahl). Nach OK-Response
  // vom Server halten wir scanProgress fest — der Auto-Refresh-Effekt oben
  // pollt dann alle 2.5s, und der Erkennungs-Effekt schaltet ihn ab sobald
  // eine Kandidatur mit lastSeenAt >= trigger reinkommt (oder Timeout).
  const handleScanTriggered = (correlationId: string, triggeredAtIso: string | null) => {
    setTriggerModalOpen(false);
    const triggeredAt = triggeredAtIso ? new Date(triggeredAtIso).getTime() : Date.now();
    setScanProgress({ correlationId, triggeredAt });
    showToast({
      tone: 'info',
      title: 'Scan läuft',
      message: 'Field-Client tastet die gewählten Interfaces ab (max. 30 s). Ergebnisse erscheinen automatisch.',
      correlationId,
      autoHideAfterMs: 5000,
    });
  };

  const triggerDiscovery = async (deviceId: string, deviceName: string) => {
    if (!plantId) return;
    try {
      const res = await api.post<{ correlationId: string }>(
        `/api/plants/${plantId}/discovery/trigger?deviceId=${encodeURIComponent(deviceId)}`,
        {});
      // Progress state → das DiscoveryRunningBanner wird sichtbar und der
      // Auto-Poller sucht nach der commited Session. Kein Toast: das Banner
      // ist die primäre Rückmeldung.
      const now = Date.now();
      setDiscoveryProgress({
        correlationId: res.correlationId,
        triggeredAt: now,
        deviceId,
        deviceName,
        lastTickAt: now,
        phase: 'triggered',
        step: 'Trigger gesendet — warte auf Field-Client…',
        completed: null,
        total: null,
      });
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        showToast({
          tone: 'warn',
          title: 'Credentials fehlen',
          message: 'Setze zuerst die Credentials des Geräts, dann kannst du Discovery starten.',
          autoHideAfterMs: 6000,
        });
      } else {
        const msg = e instanceof ApiError ? `Trigger fehlgeschlagen (${e.status}).` : 'Unbekannter Fehler.';
        showToast({ tone: 'error', title: 'Discovery-Trigger fehlgeschlagen', message: msg, autoHideAfterMs: 6000 });
      }
    }
  };

  // Discovery-Progress-Handling: KEIN Absolut-Timeout — solange Progress-
  // Ticks vom Field-Client reinkommen läuft's; erst wenn DISCOVERY_DEADBAND_MS
  // Millisekunden KEIN Tick angekommen ist gilt's als steckengeblieben.
  //
  // Zwei parallele Signalquellen:
  //   1. SignalR-Kanal `discoveryProgress` — feuert live sobald der Client
  //      einen Tick pusht. Wird als Deadband-Reset + UI-Update genutzt.
  //   2. HTTP-Polling der Session-Liste (Fallback für die "done"-Erkennung —
  //      auch wenn der SignalR-Tick verloren geht, sehen wir die commited
  //      Session hier). Läuft alle DISCOVERY_POLL_MS.
  useEffect(() => {
    if (!discoveryProgress || !plantId) return;
    let cancelled = false;
    const started = discoveryProgress.triggeredAt;
    const correlationId = discoveryProgress.correlationId;
    void subscribePlant(plantId).catch(() => { /* Fallback: Polling reicht */ });
    const unsubscribeProgress = onDiscoveryProgress((evt) => {
      if (cancelled) return;
      if (evt.correlationId !== correlationId) return;
      setDiscoveryProgress((cur) => cur && cur.correlationId === correlationId
        ? {
            ...cur,
            lastTickAt: Date.now(),
            phase: evt.phase,
            step: evt.stepDescription,
            completed: evt.completed,
            total: evt.total,
          }
        : cur);
    });
    const poll = async () => {
      try {
        const sessions = await api.get<DiscoverySessionSummary[]>(
          `/api/discovery/sessions?plantId=${plantId}`);
        if (cancelled) return;
        const hit = sessions.find((s) =>
          s.deviceId === discoveryProgress.deviceId
          && new Date(s.createdAt).getTime() >= started - 500);
        if (hit) {
          setDiscoveryProgress(null);
          showToast({
            tone: 'success',
            title: 'Discovery-Session bereit',
            message: `${discoveryProgress.deviceName}: Preview kann geprüft werden.`,
            autoHideAfterMs: 5000,
          });
          location.route(`/plants/${plantId}/discovery?deviceId=${discoveryProgress.deviceId}`);
          return;
        }
      } catch { /* transient errors ignorieren, weiterpollen */ }
    };
    void poll();
    const interval = window.setInterval(poll, DISCOVERY_POLL_MS);
    // Deadband-Timer: prüft jede Sekunde ob seit letztem Tick zu viel Zeit
    // vergangen ist. Reset passiert automatisch weil jeder Tick lastTickAt
    // hochsetzt und der Effect NICHT neu montiert wird (dieselbe
    // correlationId → dieselbe Instanz).
    const deadbandInterval = window.setInterval(() => {
      setDiscoveryProgress((cur) => {
        if (!cur || cur.correlationId !== correlationId) return cur;
        const silentFor = Date.now() - cur.lastTickAt;
        if (silentFor < DISCOVERY_DEADBAND_MS) return cur;
        showToast({
          tone: 'error',
          title: 'Kein Progress-Signal vom Field-Client',
          message: `${cur.deviceName}: seit ${Math.round(silentFor / 1000)} s keine Rückmeldung. Prüfe Client-Log.`,
          autoHideAfterMs: 10000,
          correlationId,
        });
        return null;
      });
    }, 1_000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.clearInterval(deadbandInterval);
      unsubscribeProgress();
    };
    // Bewusst NUR auf correlationId und plantId reagieren — jeder Tick
    // mutiert das Progress-Objekt aber darf den Effect NICHT neu starten
    // (sonst würden Interval + Subscription flackern).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discoveryProgress?.correlationId, plantId]);

  const clearAllCandidates = async (includeAssigned: boolean) => {
    if (!plantId) return;
    const msg = includeAssigned
      ? 'ALLE Kandidaten dieser Anlage verwerfen (auch bereits zugewiesene)? Klassifizierungen gehen verloren.'
      : 'Alle unklassifizierten Kandidaten verwerfen und neu scannen?';
    const ok = await showConfirm({
      title: 'Kandidaten verwerfen?',
      message: msg,
      confirmLabel: 'Verwerfen',
      danger: true,
    });
    if (!ok) return;
    try {
      const res = await api.delete<{ cleared: number }>(
        `/api/plants/${plantId}/scan/candidates?unassignedOnly=${includeAssigned ? 'false' : 'true'}`);
      setSelectedId(null);
      setTick((n) => n + 1);
      showToast({
        tone: 'info',
        title: 'Kandidaten verworfen',
        message: `${res.cleared} Zeile(n) soft-deleted. Du kannst jetzt neu scannen.`,
        autoHideAfterMs: 4000,
      });
    } catch (e) {
      const emsg = e instanceof ApiError ? `Verwerfen fehlgeschlagen (${e.status}).` : 'Unbekannter Fehler.';
      showToast({ tone: 'error', title: 'Verwerfen fehlgeschlagen', message: emsg, autoHideAfterMs: 6000 });
    }
  };

  const unassignDevice = async (deviceId: string) => {
    if (!plantId) return;
    const ok = await showConfirm({
      title: 'Zuweisung entfernen?',
      message: 'Das angelegte Device wird gelöscht, Credentials werden mit gelöscht, und der Kandidat wird wieder als "offen" markiert. Historie bleibt (Soft-Delete).',
      confirmLabel: 'Entfernen',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/api/plants/${plantId}/devices/${deviceId}`);
      setTick((n) => n + 1);
      showToast({
        tone: 'info',
        title: 'Zuweisung entfernt',
        message: 'Device gelöscht, Credentials weg, Kandidat wieder offen.',
        autoHideAfterMs: 5000,
      });
    } catch (e) {
      const msg = e instanceof ApiError
        ? (e.status === 403 ? 'Keine Berechtigung (braucht plants.admin).' : `Fehlgeschlagen (${e.status}).`)
        : 'Unbekannter Fehler.';
      showToast({ tone: 'error', title: 'Zuweisung entfernen fehlgeschlagen', message: msg, autoHideAfterMs: 6000 });
    }
  };

  const deleteCandidate = async (candidateId: string) => {
    if (!plantId) return;
    const ok = await showConfirm({
      title: 'Kandidat löschen?',
      message: 'Soft-Delete — kann später aus der DB reaktiviert werden.',
      confirmLabel: 'Löschen',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/api/plants/${plantId}/scan/candidates/${candidateId}`);
      setSelectedId((cur) => cur === candidateId ? null : cur);
      setTick((n) => n + 1);
    } catch (e) {
      const msg = e instanceof ApiError ? `Löschen fehlgeschlagen (${e.status}).` : 'Unbekannter Fehler.';
      showToast({ tone: 'error', title: 'Löschen fehlgeschlagen', message: msg, autoHideAfterMs: 6000 });
    }
  };

  // Remote-Web: das Fenster muss im Klick aufgehen (Popup-Blocker), deshalb
  // kein await vor openRemoteWeb.
  const openInBrowser = (c: ScanCandidateSummary, clientId: string, target: WebTarget) => {
    if (!plantId) return;
    openRemoteWeb(plantId, clientId, c.ip, target).catch((e: unknown) => {
      showToast({
        tone: 'error',
        title: 'Im Browser öffnen fehlgeschlagen',
        message: e instanceof Error ? e.message : String(e),
        autoHideAfterMs: 8000,
      });
    });
  };
  const permissions = sessionSignal.value.user?.permissions;

  if (!plantId) {
    return <AppShell title="OT"><p class="muted">Anlage aus der Route fehlt.</p></AppShell>;
  }

  const columns: ColumnDef<ScanCandidateSummary, unknown>[] = useMemo(() => [
    {
      id: 'marker',
      header: '',
      enableSorting: false,
      cell: ({ row }) => {
        const c = row.original;
        if (c.isSelf) {
          return <span class="hv-scan__marker hv-scan__marker--self"
                       title="Eigener Field-Client dieser Anlage" aria-label="Field-Client">◉</span>;
        }
        if (c.deviceId) {
          return <span class="hv-scan__marker hv-scan__marker--assigned"
                       title="Als Device zugewiesen" aria-label="Zugewiesen">✓</span>;
        }
        return <span class="hv-scan__marker" aria-hidden="true">&nbsp;</span>;
      },
    },
    {
      id: 'ip',
      accessorKey: 'ip',
      header: 'IP',
      cell: ({ row }) => <span class="mono">{row.original.ip}</span>,
      // IPs numerisch sortieren, sonst landet 192.168.170.9 hinter 192.168.170.100.
      sortingFn: (a, b) => ipCompare(a.original.ip, b.original.ip),
    },
    {
      id: 'mac',
      accessorKey: 'mac',
      header: 'MAC',
      cell: ({ row }) => (
        <span class="mono muted">{row.original.mac ?? '—'}</span>
      ),
    },
    {
      id: 'interfaceName',
      accessorKey: 'interfaceName',
      header: 'Interface',
      cell: ({ row }) => {
        // Remote-Web: Gerät hängt direkt an einem Anschluss des Clients
        // (etwa LAN2 am G100), nicht im übrigen Netz.
        const badge = directBadge(row.original);
        return (
          <>
            {row.original.interfaceName
              ? <span class="mono">{row.original.interfaceName}</span>
              : <span class="muted">—</span>}
            {badge && (
              <span class="hv-scan__direct-badge"
                    title="Das Gerät hängt direkt an diesem Anschluss des Field-Clients">{badge}</span>
            )}
          </>
        );
      },
    },
    {
      id: 'identification',
      header: 'Identifikation',
      accessorFn: (row) =>
        row.bacnetDeviceName ?? row.vendorHint ?? row.hostname ?? (row.bacnetDeviceInstance !== null ? `#${row.bacnetDeviceInstance}` : ''),
      cell: ({ row }) => formatIdentification(row.original),
    },
    {
      id: 'protocolHints',
      header: 'Protokoll',
      accessorFn: (row) => row.deviceProtocol ?? row.protocolHints.join(','),
      cell: ({ row }) => {
        // Verbundenes Gerät: nur das klassifizierte Protokoll — die Scan-
        // Hints sind reine Port-Sniff-Signale und weichen von der eigent-
        // lichen Klassifizierung ab (Climatix → hint http, protocol
        // climatix). Unklassifiziert: Hints als kommagetrennte Liste.
        if (row.original.deviceProtocol) {
          return <code class="mono">{row.original.deviceProtocol}</code>;
        }
        return row.original.protocolHints.length > 0
          ? row.original.protocolHints.map((p) => (
              <code class="mono" style={{ marginRight: '0.3em' }} key={p}>{p}</code>))
          : <span class="muted">—</span>;
      },
    },
    {
      id: 'status',
      header: 'Status',
      accessorFn: (row) => row.deviceId ? 1 : 0,
      cell: ({ row }) => row.original.deviceId
        ? <span style={{ color: 'var(--status-success, #62c26e)' }}>online</span>
        : row.original.isSelf
          ? <span class="muted">eigen</span>
          : <strong>offen</strong>,
    },
    {
      id: 'lastSeenAt',
      accessorKey: 'lastSeenAt',
      header: 'Zuletzt gesehen',
      cell: ({ row }) => <span class="mono muted">{formatRelative(row.original.lastSeenAt)}</span>,
      sortingFn: 'datetime',
    },
    {
      id: 'actions',
      header: 'Aktionen',
      enableSorting: false,
      cell: ({ row }) => {
        const c = row.original;
        if (c.isSelf) return <span class="muted">—</span>;
        if (c.deviceId && c.deviceProtocol && c.deviceName) {
          const hasCreds = devicesWithCreds.has(c.deviceId);
          const hasSess = devicesWithSessions.has(c.deviceId);
          const hasPreview = devicesWithPreviewSession.has(c.deviceId);
          // Nicht jedes Protokoll KANN Credentials haben. BACnet/IP + Modbus
          // sind auth-los; für diese Protokolle darf der Icon nie "warn"
          // (orange) zeigen — das würde ein fehlendes Setup suggerieren wo
          // nichts fehlen kann. Muss synchron zu DeviceCredentialSchemaRegistry
          // auf der Server-Seite bleiben.
          const protocolNeedsCreds = ['climatix', 'opc-ua', 'http', 'http-json', 'ozw']
            .includes(c.deviceProtocol.toLowerCase());
          const anschlussTone = protocolNeedsCreds
            ? (hasCreds ? 'ok' : 'warn')
            : 'ok';
          const anschlussLabel = protocolNeedsCreds
            ? (hasCreds ? 'Anschluss + Credentials (gesetzt)' : 'Credentials fehlen')
            : 'Anschluss (kein Auth nötig)';
          return (
            <span class="hv-scan__row-actions" onClick={(e) => e.stopPropagation()}>
              <IconBtn label={anschlussLabel}
                       onClick={() => setCredentialsFor({
                         deviceId: c.deviceId!,
                         protocol: c.deviceProtocol!,
                         displayName: c.deviceName!,
                         ip: c.ip,
                       })}
                       tone={anschlussTone}>
                <IconPlug />
              </IconBtn>
              <IconBtn label="Umbenennen"
                       onClick={() => setRenameFor({
                         deviceId: c.deviceId!,
                         currentName: c.deviceName!,
                       })}>
                <IconPencil />
              </IconBtn>
              {hasSess && (
                <IconLink href={`/plants/${plantId}/discovery?deviceId=${c.deviceId}`}
                          label={hasPreview ? 'Discovery-Preview offen' : 'Discovery-Sessions'}
                          tone={hasPreview ? 'warn' : undefined}>
                  <IconTree />
                </IconLink>
              )}
            </span>
          );
        }
        // Offener Kandidat: Klassifizieren + Löschen, bei Weboberfläche
        // und Recht remote.web zusätzlich "Im Browser öffnen".
        const web = remoteWebAction(c, permissions);
        return (
          <span class="hv-scan__row-actions" onClick={(e) => e.stopPropagation()}>
            {web && (
              <IconBtn label={remoteWebLabel(c, web.target)}
                       onClick={() => openInBrowser(c, web.clientId, web.target)}>
                <IconOpenExternal />
              </IconBtn>
            )}
            <IconBtn label="Klassifizieren"
                     onClick={() => setClassifying(c)}
                     tone="primary">
              <IconClassify />
            </IconBtn>
            <IconBtn label="Kandidat löschen"
                     onClick={() => deleteCandidate(c.id)}
                     tone="danger">
              <IconTrash />
            </IconBtn>
          </span>
        );
      },
    },
  ], [plantId, devicesWithCreds, devicesWithSessions, devicesWithPreviewSession, permissions]);

  // Sektionsspezifische Spalten. Verbundene Geräte:
  //   * IP-Zelle zusätzlich mit dem gewählten Device-Namen darunter
  //     (Techniker will die Anlage-eigene Bezeichnung sehen, nicht nur die
  //     nichtssagende IP)
  //   * "Zuletzt gesehen" fällt weg — für ein verbundenes, aktives Gerät
  //     ist der Status "online" schon die relevantere Info.
  //   * Zusätzliche Spalte "Schema": zeigt den angewandten Template-Namen
  //     mit Version, oder "Discovered Scheme" wenn kein Template gesetzt
  //     ist (Discovery-Commit oder handgebauter Baum).
  const templateColumn = useMemo<ColumnDef<ScanCandidateSummary, unknown>>(() => ({
    id: 'appliedTemplate',
    header: 'Schema',
    accessorFn: (row) => {
      if (!row.deviceId) return '';
      const info = schemaByDevice.get(row.deviceId);
      return info?.appliedTemplateName ?? '';
    },
    cell: ({ row }) => {
      if (!row.original.deviceId) return <span class="muted">—</span>;
      const info = schemaByDevice.get(row.original.deviceId);
      // Kompakt: Standard-Text (nicht fett, nicht groß), einzeilig mit
      // Ellipsis wenn's über die Spaltenbreite geht. Vollständiger Name
      // steht im title-Attribut und im Detail-Panel.
      const base = {
        fontSize: '0.8rem',
        whiteSpace: 'nowrap' as const,
        overflow: 'hidden' as const,
        textOverflow: 'ellipsis' as const,
        display: 'inline-block',
        maxWidth: '100%',
        verticalAlign: 'middle',
      };
      if (info?.appliedTemplateName) {
        const label = info.appliedTemplateVersion !== null && info.appliedTemplateVersion !== undefined
          ? `${info.appliedTemplateName} v${info.appliedTemplateVersion}`
          : info.appliedTemplateName;
        return (
          <span style={base}
                title={`Schema-Template angewandt: ${label}`}>
            {label}
          </span>
        );
      }
      return (
        <span class="muted"
              style={{ ...base, fontStyle: 'italic' }}
              title="Kein Template angewandt — Baum aus Discovery oder handgebaut">
          Discovered Scheme
        </span>
      );
    },
  }), [schemaByDevice]);

  const connectedColumns = useMemo<ColumnDef<ScanCandidateSummary, unknown>[]>(
    () => {
      const base = columns
        .filter((col) => col.id !== 'lastSeenAt')
        .map((col) => col.id === 'ip'
          ? {
              ...col,
              cell: ({ row }: { row: { original: ScanCandidateSummary } }) => (
                <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                  <span class="mono">{row.original.ip}</span>
                  {row.original.deviceName && (
                    <span class="muted" style={{ fontSize: 'var(--fs-xs)' }}>
                      {row.original.deviceName}
                    </span>
                  )}
                </div>
              ),
            }
          : col);
      // Insert the template column right before the actions column so the
      // schema origin is visible next to the "was ist das Gerät"-Cluster.
      const actionsIx = base.findIndex((c) => c.id === 'actions');
      if (actionsIx < 0) return [...base, templateColumn];
      return [...base.slice(0, actionsIx), templateColumn, ...base.slice(actionsIx)];
    },
    [columns, templateColumn],
  );

  return (
    <AppShell title="OT">
      <PlantSubNav plantId={plantId} />
      <div class="hv-scan">
        <div class="hv-scan__list">
          <DeviceClientInfo
            plantId={plantId}
            onClientsChange={(list) => setFieldClientCount(list.length)}
          />
          {scanProgress && (
            <ScanRunningBanner triggeredAt={scanProgress.triggeredAt} />
          )}
          {discoveryProgress && (
            <DiscoveryRunningBanner
              progress={discoveryProgress}
              onCancel={() => setDiscoveryProgress(null)}
            />
          )}
          {listState.status === 'loading' && candidates.length === 0 && <p class="muted">Lade Kandidaten…</p>}
          {listState.status === 'error' && <p class="muted">Kandidaten konnten nicht geladen werden.</p>}
          {listState.status === 'ok' && candidates.length === 0 && (
            <div class="hv-scan__empty-hint">
              <p>
                Noch keine Kandidaten für diese Anlage.
              </p>
              <p>
                Klick <strong>"Scan starten…"</strong> — der Feld-Client dieser Anlage tastet die
                gewählten Interfaces ab. Der Scanner probt TCP auf <code class="mono">502</code>
                {' '}(Modbus), <code class="mono">4840</code> (OPC-UA),
                {' '}<code class="mono">8443</code> (Climatix), <code class="mono">80/443</code> (HTTP)
                sowie BACnet-Who-Is auf UDP&nbsp;47808. Kandidaten werden mit Interface, MAC
                und (bei BACnet) Device-Instance samt Namen angezeigt.
              </p>
              {/* Empty-State-CTA: Der Scan-Starten-Button haengt sonst nur im
                * "Verfuegbare Geraete"-Header, und der rendert bei 0 Kandidaten
                * nicht. Ohne diesen Fallback verweist der Hinweistext auf einen
                * Button den's noch gar nicht gibt. */}
              <p>
                <button
                  type="button"
                  class="hv-btn hv-btn--primary"
                  disabled={scanProgress !== null}
                  onClick={() => setTriggerModalOpen(true)}
                >
                  {scanProgress !== null ? 'Scan läuft…' : 'Scan starten…'}
                </button>
              </p>
            </div>
          )}
          {listState.status === 'ok' && candidates.length > 0 && (
            <>
              {(() => {
                // Zwei Tabellen: verbundene (Assigned) oben, verfügbare
                // (Unassigned inkl. self-Field-Client) unten. Beide mit
                // eigener Überschrift. Selection funktioniert Cross-Tabelle
                // — nur die aktive Zeile bekommt die --active-Klasse.
                const connected = candidates.filter((c) => c.deviceId !== null);
                // Verfügbar: eigene Field-Client-IPs (self) oben, dann
                // visuellen Divider, dann echte Kandidaten mit IP DESC (aus
                // Operator-Sicht: die eigenen Interfaces sind immer da, alles
                // andere sortiert nach IP absteigend). Zwei Sub-Tabellen
                // damit ein Klick auf einen Spaltenkopf die Reihenfolge nicht
                // zerhaut.
                // Self-Interfaces des Field-Clients: nur zeigen wenn OBEN in
                // der DeviceClientInfo-Karte KEIN registrierter Field-Client
                // steht. Sobald mindestens einer bekannt ist, ist die Zeile
                // hier redundant (Karte listet Interfaces + MAC + Live-Vitals).
                const availableSelf = fieldClientCount > 0
                  ? []
                  : candidates.filter((c) => c.deviceId === null && c.isSelf);
                const availableOther = candidates.filter((c) => c.deviceId === null && !c.isSelf);
                return (
                  <>
                    <section class="hv-scan__section">
                      <h3 class="hv-scan__section-head">
                        <span>
                          Verbundene Geräte
                          <span class="muted"> ({connected.length})</span>
                        </span>
                        {(() => {
                          // Toolbar with actions for the currently selected
                          // connected device. Always rendered so the header
                          // does not jump when the operator clicks a row.
                          // Individual buttons disable themselves based on
                          // context: no selection -> all disabled with hint;
                          // schema present -> Discovery disabled, Schema
                          // entfernen enabled; no schema -> the other way.
                          const dev = selected && selected.deviceId ? selected : null;
                          const info = dev ? schemaByDevice.get(dev.deviceId!) : undefined;
                          const hasSchema = (info?.nodeCount ?? 0) > 0 || (info?.leafCount ?? 0) > 0;
                          const protoLower = (dev?.deviceProtocol ?? '').toLowerCase();
                          const protocolNeedsCreds = ['climatix', 'opc-ua', 'http', 'http-json', 'ozw'].includes(protoLower);
                          const credsMissing = dev !== null
                            && protocolNeedsCreds
                            && !devicesWithCreds.has(dev.deviceId!);
                          const displayName = dev
                            ? (dev.bacnetDeviceName ?? dev.vendorHint ?? dev.ip)
                            : '';
                          const noSel = 'Erst ein verbundenes Gerät in der Tabelle auswählen';
                          const busy = scanProgress !== null;
                          // J.17: Modbus hat auf Protokollebene keine
                          // Register-Enumeration. Der Discovery-Knopf konnte
                          // hier nie etwas finden — das war die Sackgasse, die
                          // Pazi gemeldet hat. Statt ihn anzubieten, wird er
                          // gesperrt und der Grund steht dran; die richtige
                          // Aktion ist der Template-Knopf daneben, der für
                          // Modbus als primär markiert wird.
                          const noDiscoveryReason = dev
                            ? discoveryUnavailableReason(dev.deviceProtocol)
                            : null;
                          const isModbus = isModbusFamily(dev?.deviceProtocol);
                          return (
                            <span class="hv-scan__toolbar hv-scan__toolbar--connected">
                              <ToolbarBtn
                                icon={<IconPlug />}
                                label="Credentials"
                                title={dev ? (protocolNeedsCreds ? 'Anschluss + Credentials setzen' : 'Anschluss (kein Auth nötig)') : noSel}
                                tone={dev && credsMissing ? 'warn' : undefined}
                                disabled={!dev || busy}
                                onClick={() => dev && setCredentialsFor({
                                  deviceId: dev.deviceId!,
                                  protocol: dev.deviceProtocol!,
                                  displayName,
                                  ip: dev.ip,
                                })}
                              />
                              <ToolbarBtn
                                icon={<IconDiscovery size={14} />}
                                label="Discovery"
                                title={!dev ? noSel
                                  : noDiscoveryReason ? noDiscoveryReason
                                  : hasSchema ? 'Device hat bereits ein Schema — Discovery würde nur einen Preview produzieren'
                                  : credsMissing ? 'Erst Credentials setzen, dann Discovery'
                                  : 'Discovery-Trigger an den Field-Client senden'}
                                disabled={!dev || busy || hasSchema || discoveryProgress !== null
                                  || credsMissing || noDiscoveryReason !== null}
                                onClick={() => dev && triggerDiscovery(dev.deviceId!, displayName)}
                              />
                              <ToolbarBtn
                                icon={<IconTemplateApply size={14} />}
                                label={isModbus
                                  ? (hasSchema ? 'Anderes Register-Template' : 'Register-Template')
                                  : (hasSchema ? 'Anderes Template' : 'Template')}
                                title={!dev ? noSel
                                  : isModbus
                                    ? (hasSchema
                                        ? 'Register-Karte austauschen — der einzige Weg zu einem Baum auf Modbus'
                                        : 'Register-Karte der Geräteklasse anwenden — der Weg zu einem Baum auf Modbus, '
                                          + 'ohne Netzabfrage. Templates für modbus-tcp und modbus-rtu gelten als eine Familie.')
                                  : hasSchema ? 'Aktuellen Baum durch ein anderes Template ersetzen'
                                  : 'Ein publiziertes Schema-Template auf dieses Device kippen'}
                                tone={isModbus && !hasSchema ? 'primary' : undefined}
                                disabled={!dev || busy || !dev?.deviceProtocol}
                                onClick={() => dev && dev.deviceProtocol && setApplyTemplateFor({
                                  deviceId: dev.deviceId!,
                                  protocol: dev.deviceProtocol,
                                  hasSchema,
                                })}
                              />
                              <ToolbarBtn
                                icon={<IconSchemaRemove size={14} />}
                                label="Schema löschen"
                                title={!dev ? noSel
                                  : !hasSchema ? 'Kein Schema vorhanden'
                                  : 'Alle Nodes + Leaves unter diesem Device hart löschen (Device + Credentials bleiben)'}
                                tone="danger"
                                disabled={!dev || busy || !hasSchema}
                                onClick={() => dev && deleteDeviceSchema(dev.deviceId!, displayName)}
                              />
                              <ToolbarBtn
                                icon={<IconPencil />}
                                label="Umbenennen"
                                title={dev ? 'Device umbenennen' : noSel}
                                disabled={!dev || busy}
                                onClick={() => dev && setRenameFor({
                                  deviceId: dev.deviceId!,
                                  currentName: dev.deviceName!,
                                })}
                              />
                              <ToolbarBtn
                                icon={<IconDeleteSoft size={14} />}
                                label="Zuweisung entfernen"
                                title={dev ? 'Device löschen, Credentials mit weg, Kandidat wieder offen' : noSel}
                                tone="danger"
                                disabled={!dev || busy}
                                onClick={() => dev && unassignDevice(dev.deviceId!)}
                              />
                            </span>
                          );
                        })()}
                      </h3>
                      {connected.length === 0
                        ? <p class="muted" style={{ padding: '0.5rem 0.75rem' }}>Noch nichts klassifiziert.</p>
                        : <DataTable<ScanCandidateSummary>
                            columns={connectedColumns}
                            rows={connected}
                            rowKey={(row) => row.id}
                            onRowClick={(row) => setSelectedId(row.id)}
                            selectedRowId={selectedId}
                            initialSorting={[{ id: 'ip', desc: false }]}
                            emptyLabel="Keine verbundenen Geräte."
                          />}
                    </section>
                    <section class="hv-scan__section">
                      <h3 class="hv-scan__section-head">
                        <span>
                          Verfügbare Geräte
                          <span class="muted"> ({availableSelf.length + availableOther.length})</span>
                        </span>
                        <span class="hv-scan__toolbar hv-scan__toolbar--connected">
                          {/* Selection-bezogene Aktion links: Klassifizieren
                              des aktuell ausgewählten offenen Kandidaten.
                              Disabled wenn nichts selektiert, wenn ein
                              verbundenes Gerät selektiert ist (dafür gibt es
                              die andere Toolbar), wenn's der eigene
                              Field-Client ist, oder während eines Scans. */}
                          {(() => {
                            const cand = selected && !selected.deviceId ? selected : null;
                            const noSel = 'Erst einen offenen Kandidaten in der Tabelle wählen';
                            return (
                              <ToolbarBtn
                                icon={<IconClassify />}
                                label="Klassifizieren"
                                title={!cand ? noSel
                                  : cand.isSelf ? 'Eigener Field-Client — kein Geräte-Kandidat'
                                  : scanProgress ? 'Während des Scans nicht möglich'
                                  : 'Kandidat als Device klassifizieren'}
                                tone="primary"
                                disabled={!cand || cand.isSelf || scanProgress !== null}
                                onClick={() => cand && setClassifying(cand)}
                              />
                            );
                          })()}
                          <IconBtn label={scanProgress
                            ? 'Scan läuft…'
                            : 'Netzwerk-Scan starten'}
                                   tone="primary"
                                   disabled={scanProgress !== null}
                                   onClick={() => setTriggerModalOpen(true)}>
                            <IconScan />
                          </IconBtn>
                          <IconBtn label={openCount === 0
                            ? 'Nichts zu verwerfen'
                            : scanProgress
                              ? 'Während des Scans nicht möglich'
                              : 'Alle unklassifizierten Kandidaten verwerfen'}
                                   tone="danger"
                                   disabled={scanProgress !== null || openCount === 0}
                                   onClick={() => clearAllCandidates(false)}>
                            <IconTrash />
                          </IconBtn>
                        </span>
                      </h3>
                      {availableSelf.length > 0 && (
                        <>
                          <DataTable<ScanCandidateSummary>
                            columns={columns}
                            rows={availableSelf}
                            rowKey={(row) => row.id}
                            onRowClick={(row) => setSelectedId(row.id)}
                            selectedRowId={selectedId}
                            rowClassName={(row) => row.isSelf ? 'hv-scan__row--self' : undefined}
                            emptyLabel=""
                          />
                          {availableOther.length > 0 && (
                            <div class="hv-scan__section-divider" aria-hidden="true" />
                          )}
                        </>
                      )}
                      {availableOther.length > 0 ? (
                        <DataTable<ScanCandidateSummary>
                          columns={columns}
                          rows={availableOther}
                          rowKey={(row) => row.id}
                          onRowClick={(row) => setSelectedId(row.id)}
                          selectedRowId={selectedId}
                          initialSorting={[{ id: 'ip', desc: true }]}
                          emptyLabel="Nichts Offenes."
                        />
                      ) : availableSelf.length === 0 && (
                        <p class="muted" style={{ padding: '0.5rem 0.75rem' }}>Nichts Offenes.</p>
                      )}
                    </section>
                  </>
                );
              })()}
            </>
          )}
        </div>
        <div class="hv-scan__detail">
          {selected ? (
            <div class="hv-scan__detail-card">
              <h2 class="hv-scan__detail-title">
                {selected.isSelf
                  ? <>Field-Client <span class="mono">{selected.ip}</span></>
                  : selected.deviceId
                    ? <>Gerät <span class="mono">{selected.deviceName ?? selected.ip}</span></>
                    : <>Verfügbar <span class="mono">{selected.ip}</span></>}
              </h2>
              <dl class="hv-kv">
                {selected.deviceId && selected.deviceName && (
                  <>
                    <dt>Name</dt><dd>{selected.deviceName}</dd>
                  </>
                )}
                {selected.deviceProtocol && (
                  <>
                    <dt>Protokoll</dt>
                    <dd><code class="mono">{selected.deviceProtocol}</code></dd>
                  </>
                )}
                <dt>IP</dt><dd class="mono">{selected.ip}</dd>
                <dt>MAC</dt><dd class="mono">{selected.mac ?? '—'}</dd>
                <dt>Interface</dt><dd class="mono">{selected.interfaceName ?? '—'}</dd>
                {selected.hostname && (<><dt>Hostname</dt><dd class="mono">{selected.hostname}</dd></>)}
                {selected.vendorHint && (<><dt>Vendor-Hint</dt><dd>{selected.vendorHint}</dd></>)}
                {selected.bacnetDeviceInstance !== null && (
                  <>
                    <dt>BACnet-Instance</dt><dd class="mono">{selected.bacnetDeviceInstance}</dd>
                  </>
                )}
                {selected.bacnetDeviceName && (
                  <>
                    <dt>BACnet-Name</dt><dd class="mono">{selected.bacnetDeviceName}</dd>
                  </>
                )}
                {!selected.deviceId && (
                  <>
                    <dt>Protokoll-Hints</dt><dd>{selected.protocolHints.join(', ') || '—'}</dd>
                  </>
                )}
                <dt>Erst gesehen</dt><dd class="mono muted">{formatFull(selected.firstSeenAt)}</dd>
                <dt>Zuletzt gesehen</dt><dd class="mono muted">{formatFull(selected.lastSeenAt)}</dd>
                {selected.deviceId && (() => {
                  const info = schemaByDevice.get(selected.deviceId);
                  const hasSchema = (info?.nodeCount ?? 0) > 0 || (info?.leafCount ?? 0) > 0;
                  return (
                    <>
                      <dt>Schema</dt>
                      <dd>
                        {hasSchema
                          ? <span title="Template angewandt oder Discovery-Commit"><strong>{info!.nodeCount}</strong> Nodes · <strong>{info!.leafCount}</strong> Leaves</span>
                          : <span class="muted">leer (Discovery oder Template ausstehend)</span>}
                      </dd>
                      <dt>Ursprung</dt>
                      <dd>
                        {info?.appliedTemplateName
                          ? (
                            <span>
                              <strong>{info.appliedTemplateName}</strong>
                              {info.appliedTemplateVersion !== null && info.appliedTemplateVersion !== undefined && (
                                <span class="muted" style={{ marginLeft: '0.35em' }}>v{info.appliedTemplateVersion}</span>
                              )}
                            </span>
                          )
                          : <span class="muted">Discovered Scheme</span>}
                      </dd>
                    </>
                  );
                })()}
              </dl>
              <div class="hv-scan__detail-actions">
                {/* Alle selektions-abhängigen Aktionen (Verbundene:
                    Credentials/Discovery/Template/Schema/Rename/Unassign;
                    Verfügbar: Klassifizieren) leben in der Section-Head-
                    Toolbar der jeweiligen Tabelle. Hier bleibt nur der
                    Kandidaten-Row-Delete — das ist keine device-Aktion,
                    sondern räumt die scan-candidates-Zeile weg und passt
                    daher inhaltlich zum Detail-Panel des Kandidaten. */}
                <button type="button" class="hv-btn hv-btn--danger-outline"
                        onClick={() => deleteCandidate(selected.id)}
                        disabled={scanProgress !== null}>
                  Löschen
                </button>
              </div>
            </div>
          ) : (
            <div class="hv-scan__detail-empty">
              Kandidat in der Liste wählen…
            </div>
          )}
        </div>
      </div>
      {classifying && (
        <ClassifyCandidateModal
          plantId={plantId}
          candidate={classifying}
          onClassified={(deviceId, protocol) => {
            const c = classifying;
            setClassifying(null);
            setTick((n) => n + 1);
            showToast({
              tone: 'success',
              title: 'Device angelegt',
              message: `${c.ip} → Device ${deviceId.slice(0, 8)}… — jetzt Credentials setzen.`,
              autoHideAfterMs: 6000,
            });
            // Nahtloser Übergang: direkt den Credentials-Modal öffnen.
            // Techniker klickt in einem Rutsch bis zum discovery-ready
            // Device, ohne dazwischen die Liste refreshen zu müssen.
            setCredentialsFor({
              deviceId,
              protocol,
              displayName: c.bacnetDeviceName ?? c.vendorHint ?? c.ip,
              ip: c.ip,
            });
          }}
          onClose={() => setClassifying(null)}
        />
      )}
      {credentialsFor && (
        <CredentialsModal
          deviceId={credentialsFor.deviceId}
          protocol={credentialsFor.protocol}
          deviceDisplayName={credentialsFor.displayName}
          hostHint={credentialsFor.ip}
          plantId={plantId}
          onSaved={() => {
            const ctx = credentialsFor;
            setCredentialsFor(null);
            showToast({
              tone: 'success',
              title: 'Credentials gespeichert',
              message: `${ctx.displayName} ist bereit für Discovery.`,
              autoHideAfterMs: 5000,
            });
          }}
          onSchemaApplied={(result) => {
            // Refresh der Kandidatenliste, weil ein frisch angewandtes
            // Template dem Device neue Nodes/Leaves gibt und die
            // Assigned-Anzeige davon abhängt.
            setTick((n) => n + 1);
            showToast({
              tone: 'success',
              title: 'Template angewendet',
              message:
                `+${result.nodesCreated} Nodes, +${result.leavesCreated} Leaves`
                + (result.nodesDeleted + result.leavesDeleted > 0
                  ? ` (${result.nodesDeleted} Nodes / ${result.leavesDeleted} Leaves ersetzt)`
                  : '')
                + (result.credentialCopied ? ' — Credentials übernommen.' : '.'),
              autoHideAfterMs: 6000,
            });
          }}
          onClose={() => setCredentialsFor(null)}
        />
      )}
      {applyTemplateFor && plantId && (
        <ApplySchemaTemplateModal
          plantId={plantId}
          deviceId={applyTemplateFor.deviceId}
          deviceProtocol={applyTemplateFor.protocol}
          isTargetEmpty={!applyTemplateFor.hasSchema}
          onApplied={(result) => {
            setTick((n) => n + 1);
            showToast({
              tone: 'success',
              title: 'Template angewendet',
              message:
                `+${result.nodesCreated} Nodes, +${result.leavesCreated} Leaves`
                + (result.nodesDeleted + result.leavesDeleted > 0
                  ? ` (${result.nodesDeleted} Nodes / ${result.leavesDeleted} Leaves ersetzt)`
                  : '')
                + (result.credentialCopied ? ' — Credentials übernommen.' : '.'),
              autoHideAfterMs: 6000,
            });
          }}
          onClose={() => setApplyTemplateFor(null)}
        />
      )}
      {triggerModalOpen && (
        <TriggerScanModal
          plantId={plantId}
          onTriggered={handleScanTriggered}
          onClose={() => setTriggerModalOpen(false)}
        />
      )}
      {renameFor && (
        <RenameDeviceModal
          plantId={plantId}
          deviceId={renameFor.deviceId}
          currentName={renameFor.currentName}
          currentSymbol=""
          onSaved={() => {
            setRenameFor(null);
            setTick((n) => n + 1);
            showToast({
              tone: 'success',
              title: 'Device umbenannt',
              message: 'Der neue Name gilt sofort im UI.',
              autoHideAfterMs: 4000,
            });
          }}
          onClose={() => setRenameFor(null)}
        />
      )}
    </AppShell>
  );
}

/** Best-guess identification string for the tabular view: prefer the
 *  BACnet-Name if we got one from the I-Am peer-probe, then the vendor
 *  hint, then the hostname, then the BACnet instance in bare form.
 *  Reduces the "which of these five DEOS is the pool controller" pain. */
function formatIdentification(c: ScanCandidateSummary) {
  if (c.isSelf) return <span class="hv-scan__self-label">Field-Client (dieser Anlage)</span>;
  if (c.bacnetDeviceName) return <span>{c.bacnetDeviceName}</span>;
  if (c.vendorHint) return <span>{c.vendorHint}</span>;
  if (c.hostname) return <span class="mono">{c.hostname}</span>;
  if (c.bacnetDeviceInstance !== null) return <span class="mono muted">BACnet #{c.bacnetDeviceInstance}</span>;
  return <span class="muted">—</span>;
}

/** Live-Balken während ein Scan läuft. Zeigt vergangene Sekunden und
 *  eine schmale animierte Progress-Line — reine UI-Anzeige, kein Server-
 *  round-trip. Der Elternteil schaltet den Banner ab sobald mindestens
 *  ein Kandidat mit lastSeenAt >= triggeredAt reingekommen ist. */
function DiscoveryRunningBanner({ progress, onCancel }: {
  progress: DiscoveryProgress; onCancel(): void;
}) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(id);
  }, []);
  const elapsed = Math.max(0, now - progress.triggeredAt);
  const silentFor = Math.max(0, now - progress.lastTickAt);
  // Determinater Balken wenn total bekannt, sonst indeterminate (Streifen).
  const hasProgress = progress.total !== null && progress.total > 0 && progress.completed !== null;
  const pct = hasProgress
    ? Math.min(100, Math.round(((progress.completed ?? 0) / (progress.total ?? 1)) * 100))
    : null;
  // Deadband-Warnung: sichtbar wenn zwischen 15s und 30s seit letztem Tick,
  // dann kippt der Effect eh in "steckengeblieben".
  const deadbandWarning = silentFor > DISCOVERY_DEADBAND_MS / 2;
  return (
    <div class="hv-scan__running">
      <div class="hv-scan__running-head">
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          <DiscoveryRadar size={28} style={{ color: 'var(--status-info)', flex: 'none' }} />
          <span>
            Discovery <b>{progress.deviceName}</b> — <span class="mono">{progress.phase}</span>
            {' · '}{progress.step}
            {hasProgress && <> ({progress.completed} / {progress.total})</>}
          </span>
        </span>
        <span style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <span class="mono muted">{Math.floor(elapsed / 1000)} s</span>
          {deadbandWarning && (
            <span style={{ color: 'var(--status-warn)', fontSize: 'var(--fs-xs)' }}>
              seit {Math.floor(silentFor / 1000)} s still…
            </span>
          )}
          <button type="button" class="hv-btn hv-btn--tight" onClick={onCancel}>Ausblenden</button>
        </span>
      </div>
      <div class="hv-scan__running-track">
        {pct !== null ? (
          <div class="hv-scan__running-bar" style={{ width: `${pct}%` }} />
        ) : (
          // Indeterminate: schmaler Balken der von rechts nach links wandert.
          <div class="hv-scan__running-bar" style={{
            width: '20%',
            transform: `translateX(${((elapsed / 30) % 400) - 100}%)`,
            transition: 'transform 0.3s linear',
          }} />
        )}
      </div>
    </div>
  );
}

function ScanRunningBanner({ triggeredAt }: { triggeredAt: number }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(id);
  }, []);
  const elapsed = Math.max(0, now - triggeredAt);
  const pct = Math.min(100, (elapsed / SCAN_TIMEOUT_MS) * 100);
  return (
    <div class="hv-scan__running">
      <div class="hv-scan__running-head">
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          <NetworkScan size={22} style={{ color: 'var(--status-info)', flex: 'none' }} />
          <span>Scan läuft… (Field-Client tastet die gewählten Interfaces ab)</span>
        </span>
        <span class="mono muted">{Math.floor(elapsed / 1000)} s</span>
      </div>
      <div class="hv-scan__running-track">
        <div class="hv-scan__running-bar" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/** Numeric IPv4 compare so 192.168.170.9 sorts before 192.168.170.100
 *  instead of lexicographic ordering (`.100` before `.9`). Non-IPv4 falls
 *  back to plain string compare so a hostname-only row still has a stable
 *  order. */
function ipCompare(a: string, b: string): number {
  const pa = parseIp(a);
  const pb = parseIp(b);
  if (pa === null || pb === null) return a.localeCompare(b);
  return pa - pb;
}
function parseIp(s: string): number | null {
  const parts = s.split('.');
  if (parts.length !== 4) return null;
  let n = 0;
  for (const p of parts) {
    const v = Number(p);
    if (!Number.isInteger(v) || v < 0 || v > 255) return null;
    n = n * 256 + v;
  }
  return n;
}

// ---- Icon-Buttons für Aktionen-Spalte ------------------------------------
//
// Kompakte lucide-style SVGs, 16px, `currentColor` — Farbe kommt aus der
// Button-CSS-Klasse (tone: default/ok/warn/danger/primary). Für den
// Discovery-Link nutzen wir dieselben Icons via `IconLink`.

function IconBtn({ label, tone, disabled, onClick, children }: {
  label: string;
  tone?: 'ok' | 'warn' | 'danger' | 'primary';
  disabled?: boolean;
  onClick(): void;
  children: preact.ComponentChildren;
}) {
  const cls = tone ? `hv-icon-btn hv-icon-btn--${tone}` : 'hv-icon-btn';
  return (
    <button type="button" class={cls} title={label} aria-label={label}
            disabled={disabled} onClick={onClick}>
      {children}
    </button>
  );
}
function IconLink({ label, tone, href, children }: {
  label: string;
  tone?: 'ok' | 'warn' | 'danger' | 'primary';
  href: string;
  children: preact.ComponentChildren;
}) {
  const cls = tone ? `hv-icon-btn hv-icon-btn--${tone}` : 'hv-icon-btn';
  return (
    <a class={cls} title={label} aria-label={label} href={href}>
      {children}
    </a>
  );
}

// Compact toolbar button with icon + short label side by side. Sits in the
// "Verbundene Geräte" section-head. Kept small and neutral so a whole row of
// them does not look like a strip of oversized primary buttons — that was
// the exact complaint that motivated the refactor.
function ToolbarBtn({ icon, label, title, tone, disabled, onClick }: {
  icon: preact.ComponentChildren;
  label: string;
  title?: string;
  tone?: 'ok' | 'warn' | 'danger' | 'primary';
  disabled?: boolean;
  onClick(): void;
}) {
  const cls = tone
    ? `hv-scan__tb-btn hv-scan__tb-btn--${tone}`
    : 'hv-scan__tb-btn';
  return (
    <button type="button" class={cls} title={title ?? label}
            disabled={disabled} onClick={onClick}>
      <span class="hv-scan__tb-btn-icon" aria-hidden="true">{icon}</span>
      <span class="hv-scan__tb-btn-label">{label}</span>
    </button>
  );
}

function IconOpenExternal() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M14 3h7v7" /><path d="M10 14 21 3" />
      <path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" />
    </svg>
  );
}
function IconPlug() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M9 2v6" /><path d="M15 2v6" />
      <path d="M6 8h12v4a6 6 0 1 1-12 0V8Z" />
      <path d="M12 18v4" />
    </svg>
  );
}
function IconPencil() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" />
    </svg>
  );
}
function IconTree() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <circle cx="5" cy="6" r="2" /><circle cx="5" cy="18" r="2" /><circle cx="19" cy="12" r="2" />
      <path d="M7 6h4a4 4 0 0 1 4 4v0" /><path d="M7 18h4a4 4 0 0 0 4-4v0" />
    </svg>
  );
}
function IconClassify() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1" fill="currentColor" />
    </svg>
  );
}
function IconTrash() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M3 6h18" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <path d="M6 6l1 14a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-14" />
    </svg>
  );
}
function IconScan() {
  // Radar-Icon: konzentrische Bögen mit Impuls-Linien in eine Richtung.
  // Signalisiert "Netzwerk sondieren" statt eines generischen Play-Buttons.
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M4.6 4.6a11 11 0 0 1 15.6 15.6" />
      <path d="M7.4 7.4a7 7 0 0 1 9.9 9.9" />
      <path d="M10.2 10.2a3 3 0 0 1 4.2 4.2" />
      <circle cx="12" cy="12" r="0.8" fill="currentColor" />
    </svg>
  );
}

// Ehemalige OtDevice/OtDevicesView/PROTOCOLS_WITH_CREDENTIALS-Trümmer sind
// entfernt — der konsolidierte View oben zeigt Verbundene + Verfügbare
// Geräte in einer Tabelle mit Icon-Aktionen; die alte separate Devices-
// Sub-Tab ist damit obsolet.

function RenameDeviceModal({
  plantId,
  deviceId,
  currentName,
  onSaved,
  onClose,
}: {
  plantId: string;
  deviceId: string;
  currentName: string;
  currentSymbol: string;
  onSaved(): void;
  onClose(): void;
}) {
  const [name, setName] = useState(currentName);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await fetch(`/api/plants/${plantId}/devices/${deviceId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ name: name.trim() }),
      }).then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
      });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unbekannter Fehler');
      setSaving(false);
    }
  };
  return (
    <div class="hv-modal-backdrop" onClick={() => !saving && onClose()}>
      <div class="hv-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '30rem' }}>
        <div class="hv-modal__header">
          <h2 class="hv-modal__title">Device umbenennen</h2>
          <button type="button" class="hv-modal__close" onClick={onClose} disabled={saving}>×</button>
        </div>
        <div class="hv-modal__body">
          <div class="hv-modal__field">
            <label>Name</label>
            <input type="text" value={name}
                   onInput={(e) => setName((e.currentTarget as HTMLInputElement).value)}
                   disabled={saving} autoFocus />
          </div>
          <p class="muted" style={{ fontSize: 'var(--fs-xs)', marginTop: 0 }}>
            Device-Identität ist die interne GUID <code class="mono">{deviceId.slice(0, 8)}…</code>. Der
            Name ist reine Anzeige und darf jederzeit geändert werden.
          </p>
          {error && <p class="hv-modal__error">{error}</p>}
        </div>
        <div class="hv-modal__footer">
          <button type="button" class="hv-btn" onClick={onClose} disabled={saving}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconCancel size={14} />Abbrechen</span>
          </button>
          <button type="button" class="hv-btn hv-btn--primary" onClick={save}
                  disabled={saving || !name.trim()}>
            {saving ? 'Speichere…' : (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconSave size={14} />Speichern</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatRelative(iso: string): string {
  const d = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.floor((now - d) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}
function formatFull(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear() % 100} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}
